import { useState, useEffect, useCallback } from "react";
import { Calendar, Clock, Check, Plus, ChevronLeft, ChevronRight, DoorOpen, Sparkles, Lock, Trash2, Monitor } from "lucide-react";
import AppShell from "../components/layout/AppShell";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { ScheduleCard, RoomCard, EventCard } from "../components/agenda/AgendaCards";
import MiniCalendar from "../components/agenda/MiniCalendar";
import CreateScheduleModal from "../components/agenda/CreateScheduleModal";
import RoomBookingModal from "../components/agenda/RoomBookingModal";
import EventModal from "../components/agenda/EventModal";
import BlockRoomModal from "../components/agenda/BlockRoomModal";

var MONTHS = ["Janeiro","Fevereiro","Marco","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

function isSameDay(a, b) { return a.getFullYear()===b.getFullYear()&&a.getMonth()===b.getMonth()&&a.getDate()===b.getDate(); }
function dateToStr(d) { return d.toISOString().split("T")[0]; }
function fmtDate(d) { if(!d)return ""; try{return new Date(d).toLocaleDateString("pt-BR",{weekday:"long",day:"numeric",month:"long"});}catch(e){return "";} }

export default function AgendaPage() {
  var auth = useAuth();
  var navigate = useNavigate();
  var isAdmin = auth.user ? auth.user.role === "admin" : false;
  var userId = auth.user ? auth.user.id : null;

  const [schedules, setSchedules] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [events, setEvents] = useState([]);
  const [slots, setSlots] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [curMonth, setCurMonth] = useState(new Date());
  const [selDate, setSelDate] = useState(new Date());
  const [modal, setModal] = useState(null);

  var fetchAll = useCallback(async function() {
    setLoading(true);
    try {
      var r = await Promise.all([api.get("/schedule?limit=100"),api.get("/room/bookings"),api.get("/events"),api.get("/room/blocks")]);
      setSchedules(r[0].data.schedules||[]);
      setRooms(r[1].data.bookings||[]);
      setEvents(r[2].data.events||[]);
      setBlocks(r[3].data.blocks||[]);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(function() { fetchAll(); }, [fetchAll]);

  var selStr = dateToStr(selDate);
  useEffect(function() {
    api.get("/room/slots?target_date="+selStr).then(function(r){setSlots(r.data.slots||[]);}).catch(function(){setSlots([]);});
  }, [selStr]);

  function goMonth(d) { setCurMonth(new Date(curMonth.getFullYear(), curMonth.getMonth()+d, 1)); }
  function closeAndRefresh() { setModal(null); fetchAll(); }

  var daySch = schedules.filter(function(s) { try { return isSameDay(new Date(s.date), selDate); } catch(e) { return false; } });
  var dayRm = rooms.filter(function(b) { return b.date === selStr; });
  var dayEv = events.filter(function(v) { return v.start_date <= selStr && (v.end_date || v.start_date) >= selStr; });
  var dayBlk = blocks.find(function(b) { return b.start_date <= selStr && b.end_date >= selStr; });
  var now = new Date();
  var wk = new Date(now.getTime() + 604800000);
  var upcoming = schedules.filter(function(s) { try { var d=new Date(s.date); return d>=now&&d<=wk&&s.status==="agendado"; } catch(e) { return false; } });
  var nAg=0,nRe=0,nNo=0;
  schedules.forEach(function(s) { if(s.status==="agendado")nAg++;if(s.status==="realizado")nRe++;if(s.status==="no_show")nNo++; });
  var nRm = rooms.filter(function(b) { return b.status==="reservado"; }).length;
  var mLabel = MONTHS[curMonth.getMonth()]+" "+curMonth.getFullYear();

  return (
    <AppShell activePage="agenda" title="Agenda">
      <div data-testid="agenda-page">
        {modal==="schedule"&&<CreateScheduleModal onClose={function(){setModal(null);}} onSave={closeAndRefresh}/>}
        {modal==="room"&&<RoomBookingModal onClose={function(){setModal(null);}} onSave={closeAndRefresh} selectedDate={selDate} slots={slots}/>}
        {modal==="event"&&<EventModal onClose={function(){setModal(null);}} onSave={closeAndRefresh}/>}
        {modal==="block"&&<BlockRoomModal onClose={function(){setModal(null);}} onSave={closeAndRefresh}/>}
        <KPIRow nAg={nAg} nRe={nRe} nNo={nNo} nRm={nRm}/>
        <div className="flex justify-end mb-4">
          <button onClick={function(){navigate("/agenda/tv");}}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0B1120] text-white rounded-xl text-sm font-medium hover:bg-[#162033] transition-colors shadow-lg"
            data-testid="agenda-tv-button">
            <Monitor className="w-4 h-4"/> Exibir na TV
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          <LeftPanel curMonth={curMonth} mLabel={mLabel} goMonth={goMonth} schedules={schedules} rooms={rooms} events={events} selDate={selDate} setSelDate={setSelDate} setModal={setModal} isAdmin={isAdmin} blocks={blocks} onDelBlock={function(id){api.delete("/room/blocks/"+id).then(fetchAll);}}/>
          <RightPanel selDate={selDate} daySch={daySch} dayRm={dayRm} dayEv={dayEv} dayBlk={dayBlk} upcoming={upcoming} loading={loading} isAdmin={isAdmin} userId={userId}
            onStatus={function(id,s){api.put("/schedule/"+id,{status:s}).then(fetchAll);}}
            onDelSch={function(id){api.delete("/schedule/"+id).then(fetchAll);}}
            onCancelRm={function(id){api.delete("/room/bookings/"+id).then(fetchAll);}}
            onDelEv={function(id){api.delete("/events/"+id).then(fetchAll);}}/>
        </div>
      </div>
    </AppShell>
  );
}

function KPIRow({ nAg, nRe, nNo, nRm }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6">
      <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-2"><div className="p-2 bg-[#3B82F6]/10 rounded-xl"><Calendar className="w-4 h-4 text-[#3B82F6]"/></div><span className="text-xs text-gray-500">Agendados</span></div>
        <p className="text-2xl font-bold text-[#0A2F3D]" data-testid="agenda-kpi-agendado">{nAg}</p>
      </div>
      <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-2"><div className="p-2 bg-emerald-500/10 rounded-xl"><Check className="w-4 h-4 text-emerald-500"/></div><span className="text-xs text-gray-500">Realizados</span></div>
        <p className="text-2xl font-bold text-emerald-600" data-testid="agenda-kpi-realizado">{nRe}</p>
      </div>
      <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-2"><div className="p-2 bg-red-500/10 rounded-xl"><Clock className="w-4 h-4 text-red-500"/></div><span className="text-xs text-gray-500">No-show</span></div>
        <p className="text-2xl font-bold text-red-600" data-testid="agenda-kpi-noshow">{nNo}</p>
      </div>
      <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-2"><div className="p-2 bg-amber-500/10 rounded-xl"><DoorOpen className="w-4 h-4 text-amber-500"/></div><span className="text-xs text-gray-500">Sala Reservada</span></div>
        <p className="text-2xl font-bold text-amber-600" data-testid="agenda-kpi-room">{nRm}</p>
      </div>
    </div>
  );
}

function LeftPanel({ curMonth, mLabel, goMonth, schedules, rooms, events, selDate, setSelDate, setModal, isAdmin, blocks, onDelBlock }) {
  return (
    <div className="lg:col-span-1 space-y-4">
      <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <button onClick={function(){goMonth(-1);}} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="cal-prev-month"><ChevronLeft className="w-4 h-4 text-gray-500"/></button>
          <h3 className="text-sm font-semibold text-[#0A2F3D]" data-testid="cal-month-label">{mLabel}</h3>
          <button onClick={function(){goMonth(1);}} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="cal-next-month"><ChevronRight className="w-4 h-4 text-gray-500"/></button>
        </div>
        <MiniCalendar currentDate={curMonth} schedules={schedules} roomBookings={rooms} events={events} onDateSelect={setSelDate} selectedDate={selDate}/>
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#3B82F6]"/><span className="text-xs text-gray-400">Agenda</span></div>
          <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"/><span className="text-xs text-gray-400">Sala</span></div>
          <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-500"/><span className="text-xs text-gray-400">Evento</span></div>
        </div>
      </div>
      <div className="space-y-2">
        <button onClick={function(){setModal("schedule");}} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#3B82F6] text-white rounded-xl text-sm font-medium hover:bg-[#2563EB]" data-testid="agenda-new-button"><Plus className="w-4 h-4"/> Novo Agendamento</button>
        <button onClick={function(){setModal("room");}} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 text-white rounded-xl text-sm font-medium hover:bg-amber-600" data-testid="room-new-button"><DoorOpen className="w-4 h-4"/> Reservar Sala</button>
        {isAdmin&&(
          <>
            <button onClick={function(){setModal("event");}} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700" data-testid="event-new-button"><Sparkles className="w-4 h-4"/> Novo Evento</button>
            <button onClick={function(){setModal("block");}} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-red-300 text-red-500 rounded-xl text-sm font-medium hover:bg-red-50" data-testid="block-room-button"><Lock className="w-4 h-4"/> Bloquear Sala</button>
          </>
        )}
      </div>
      {isAdmin&&blocks.length>0&&(
        <div className="bg-white rounded-xl lg:rounded-2xl p-4 shadow-sm">
          <h4 className="text-sm font-semibold text-[#0A2F3D] mb-3">Bloqueios Ativos</h4>
          <div className="space-y-2">{blocks.map(function(b){return(
            <div key={b.id} className="flex items-center justify-between bg-red-50 rounded-lg px-3 py-2" data-testid={"block-item-"+b.id}>
              <div><p className="text-xs font-medium text-red-700">{b.reason}</p><p className="text-xs text-red-500">{b.start_date}{b.end_date!==b.start_date?" a "+b.end_date:""}</p></div>
              <button onClick={function(){onDelBlock(b.id);}} className="p-1 hover:bg-red-100 rounded text-red-400 hover:text-red-600" data-testid={"block-delete-"+b.id}><Trash2 className="w-3.5 h-3.5"/></button>
            </div>);})}</div>
        </div>
      )}
    </div>
  );
}

function RightPanel({ selDate, daySch, dayRm, dayEv, dayBlk, upcoming, loading, isAdmin, userId, onStatus, onDelSch, onCancelRm, onDelEv }) {
  return (
    <div className="lg:col-span-2 space-y-4">
      <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
        <h3 className="text-base font-bold text-[#0A2F3D] mb-1" data-testid="day-schedule-title">{fmtDate(selDate)}</h3>
        <p className="text-xs text-gray-400 mb-4">
          {daySch.length} agendamento{daySch.length!==1?"s":""}
          {dayRm.length>0&&(" · "+dayRm.length+" reserva"+(dayRm.length!==1?"s":"")+" sala")}
          {dayEv.length>0&&(" · "+dayEv.length+" evento"+(dayEv.length!==1?"s":""))}
        </p>
        {dayBlk&&<div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4" data-testid="day-blocked-banner"><Lock className="w-4 h-4 text-red-500"/><span className="text-sm text-red-700 font-medium">Sala bloqueada: {dayBlk.reason}</span></div>}
        {dayEv.length>0&&<div className="space-y-3 mb-4">{dayEv.map(function(v){return <EventCard key={v.id} item={v} isAdmin={isAdmin} onDelete={onDelEv}/>;})}</div>}
        {dayRm.length>0&&<div className="space-y-3 mb-4">{dayRm.map(function(b){return <RoomCard key={b.id} item={b} canCancel={isAdmin||b.broker_id===userId} onCancel={onCancelRm}/>;})}</div>}
        {loading ? <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin"/></div>
        : daySch.length===0&&dayRm.length===0&&dayEv.length===0 ? <div className="text-center py-8"><Calendar className="w-10 h-10 text-gray-200 mx-auto mb-2"/><p className="text-sm text-gray-400">Nenhuma atividade para este dia</p></div>
        : <div className="space-y-3" data-testid="day-schedules-list">{daySch.map(function(s){return <ScheduleCard key={s.id} item={s} isAdmin={isAdmin} onStatusChange={onStatus} onDelete={onDelSch}/>;})}</div>}
      </div>
      {upcoming.length>0&&(
        <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
          <h3 className="text-base font-bold text-[#0A2F3D] mb-1">Proximos 7 dias</h3>
          <p className="text-xs text-gray-400 mb-4">{upcoming.length} pendente{upcoming.length!==1?"s":""}</p>
          <div className="space-y-3" data-testid="upcoming-schedules-list">{upcoming.map(function(s){return <ScheduleCard key={s.id} item={s} isAdmin={isAdmin} onStatusChange={onStatus} onDelete={onDelSch}/>;})}</div>
        </div>
      )}
    </div>
  );
}
